import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  CheckCircle2, Clock, Send, Play, AlertTriangle, Activity,
  MessageSquare, Ticket, Timer, Plus
} from 'lucide-react';
import PipelineTabs from '@/components/PipelineTabs';
import ReviewSubmitDialog from '@/components/ReviewSubmitDialog'; // eslint-disable-line no-unused-vars
import ReferencesPanel from '@/components/ReferencesPanel';
import WorkCard from '@/components/WorkCard';
import { MyTasksList } from '@/components/MyTasksRow';
import InvoiceBuilderModal, { InvoiceList } from '@/components/InvoiceBuilderModal';
import useRealtime from '@/hooks/useRealtime';
import RevisionsInboxWidget from '@/components/RevisionsInboxWidget';

const TAB_MAP = { '/fl-tasks': 'tasks', '/fl-tracker': 'tracker', '/fl-earnings': 'earnings', '/fl-communicate': 'communicate' };

// Backend tasks expose `title` (not `name`) — show a readable label with
// graceful fallbacks so the dashboard never shows "(unnamed task)" for
// CBA-derived rows.
const taskLabel = (t) => t.title || t.name || t.parent_code
  || (t.task_type ? t.task_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Task');

export default function FreelancerDashboardNew() {
  const { api, user } = useAuth();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState(TAB_MAP[location.pathname] || 'status');
  const [deptTab, setDeptTab] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [ticketText, setTicketText] = useState('');
  const [ticketSent, setTicketSent] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [workOrders, setWorkOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceBuilder, setShowInvoiceBuilder] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const navigate = useNavigate();

  const load = () => { api.get('/tasks').then(r => setTasks(r.data)).catch(() => {}); };
  const loadInvoices = () => api.get('/invoices/mine').then(r => setInvoices(r.data || [])).catch(() => setInvoices([]));
  // Load ALL WOs (incl. issued/pending) — founder ask Feb 14, 2026: Raj
  // (Rigger freelancer) couldn't see his issued WO because we only
  // surfaced 'accepted' status. That's a silent dead-end: backend hides
  // his tasks until he accepts, but he never SEES the WO to accept it.
  const loadWorkOrders = () => api.get('/work-orders').then(r =>
    setWorkOrders(r.data || [])).catch(() => setWorkOrders([]));
  const pendingWOs = workOrders.filter(w =>
    w.status === 'issued' || w.status === 'pending');
  const acceptedWOs = workOrders.filter(w => w.status === 'accepted');

  const acceptWO = async (wo) => {
    try {
      await api.put(`/work-orders/${wo.id}/status`, { status: 'accepted' });
      // Re-fetch so the freshly-accepted WO drops out of the pending banner
      // AND the previously-hidden tasks appear in the task list.
      loadWorkOrders();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to accept work order');
    }
  };

  useEffect(() => { load(); loadInvoices(); loadWorkOrders(); }, [api]);

  // PIPELINE REFORM R8 — Live push: refetch the task list whenever the
  // backend emits an event for THIS user (task assigned, auto-promoted, or
  // a submission of theirs gets a verdict).
  useRealtime(React.useCallback((evt) => {
    if (!evt) return;
    const t = evt.type;
    if (t === 'task_assigned' || t === 'task_auto_promoted'
        || t === 'submission_approved' || t === 'submission_rejected'
        || t === 'submission_revise') {
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]));

  const updateTask = async (taskId, status, progress) => {
    setUpdating(taskId);
    await api.put(`/tasks/${taskId}`, { status, progress });
    load();
    setUpdating(null);
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    await api.post('/ai/chat', { message: `[Message to Production Manager] ${msgText}`, context: 'general' });
    setMsgSent(true); setMsgText('');
    setTimeout(() => setMsgSent(false), 3000);
  };

  const raiseTicket = async () => {
    if (!ticketText.trim()) return;
    await api.post('/disputes', { description: ticketText, type: 'scope', project_id: tasks[0]?.project_id || '' });
    setTicketSent(true); setTicketText('');
    setTimeout(() => setTicketSent(false), 3000);
  };

  const filtered = deptTab === 'all' ? tasks : tasks.filter(t => t.task_type === deptTab);
  const completed = filtered.filter(t => t.status === 'completed');
  const inProgress = filtered.filter(t => t.status === 'in_progress');
  const pending = filtered.filter(t => t.status === 'not_started');
  const review = filtered.filter(t => t.status === 'review');
  const blocked = filtered.filter(t => t.status === 'blocked');
  const totalEarned = completed.reduce((s, t) => s + (t.rate_amount || 0), 0);

  // Role-aware: for client roles, this page is reachable only from the
  // sidebar's "Message Center" button — they should NOT see freelancer-only
  // tabs (My Tasks / Earnings / Tracker / References) or the freelancer
  // welcome subtitle. Force-snap them into the Communicate tab.
  const isClient = user?.role === 'client_producer' || user?.role === 'client_director';
  const isVendorArtist = user?.role === 'vendor_artist';
  React.useEffect(() => {
    if (isClient && tab !== 'communicate') setTab('communicate');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient]);

  return (
    <div className="space-y-4" data-testid="freelancer-dashboard">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Welcome, {user?.full_name}</h1>
          <p className="text-xs text-zinc-500 mt-1">
            {isClient
              ? 'Message Center — chat with the production team'
              : isVendorArtist
                ? 'Vendor Artist Dashboard — Your assigned vendor-team tasks and communication'
                : 'Freelancer Dashboard — Your tasks, earnings, and communication'}
          </p>
        </div>
        {/* "Submit for Approval" CTA removed May 24, 2026 (founder spec):
            artists submit ONLY via the inline SUBMIT button on each task
            row in MY TASKS. No separate submit surface. */}
      </div>

      {/* Revisions inbox — pin-notes from CG Sup / clients on submissions
          that need to be re-worked. Shown to artists/freelancers so they
          know what to fix without digging through the review player. */}
      {!isClient && <RevisionsInboxWidget />}

      {/* ── PENDING WORK ORDERS BANNER ───
          Founder bug Feb 14, 2026: Raj (Rigger freelancer) got a Rigging
          WO but couldn't see his tasks. Root cause: backend hides tasks
          on projects with unaccepted WOs, AND the dashboard only loaded
          'accepted' WOs — silent dead-end. This banner makes pending
          WOs impossible to miss with Accept / Decline buttons. */}
      {!isClient && pendingWOs.length > 0 && (
        <div
          className="bg-amber-500/10 border-2 border-amber-500/40 rounded-md p-4 space-y-3"
          data-testid="pending-wo-banner"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-sm font-bold text-amber-300">
                You have {pendingWOs.length} pending Work Order
                {pendingWOs.length > 1 ? 's' : ''} awaiting your action
              </p>
              <p className="text-[11px] text-amber-200/80">
                Accept to start working — your tasks are hidden until you accept.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {pendingWOs.map(wo => (
              <div
                key={wo.id}
                className="bg-zinc-950/60 border border-amber-500/20 rounded p-3 flex flex-wrap items-center gap-3 justify-between"
                data-testid={`pending-wo-${wo.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-zinc-200 font-mono">
                      {wo.work_order_no || wo.wo_no || 'WO'}
                    </span>
                    <span className="text-xs text-zinc-300">
                      {wo.project_name || wo.project_id?.slice(0, 8) || '—'}
                    </span>
                    <span className="text-[10px] text-zinc-500">·</span>
                    <span className="text-[11px] text-amber-300 capitalize">
                      {(wo.scope_type || wo.kind || 'work').replace(/_/g, ' ')}
                    </span>
                    {wo.total_amount > 0 && (
                      <>
                        <span className="text-[10px] text-zinc-500">·</span>
                        <span className="text-[11px] text-emerald-300 font-mono">
                          ₹{Number(wo.total_amount).toLocaleString('en-IN')}
                        </span>
                      </>
                    )}
                    {Array.isArray(wo.items) && wo.items.length > 0 && (
                      <>
                        <span className="text-[10px] text-zinc-500">·</span>
                        <span className="text-[10px] text-zinc-400">
                          {wo.items.length} item{wo.items.length > 1 ? 's' : ''}
                        </span>
                      </>
                    )}
                  </div>
                  {wo.deadline && (
                    <p className="text-[10px] text-zinc-500 mt-0.5">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Due: {new Date(wo.deadline).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                    data-testid={`view-wo-${wo.id}`}
                    className="h-8 px-3 text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => acceptWO(wo)}
                    data-testid={`accept-wo-${wo.id}`}
                    className="h-8 px-3 text-[11px] bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold rounded flex items-center gap-1"
                  >
                    <CheckCircle2 className="w-3 h-3" /> Accept &amp; Start
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inner tab strip removed May 24, 2026 (founder spec — redundancy).
          The sidebar entries (Current Status / My Tasks / Tracker / Earnings /
          Communicate) drive navigation now; `tab` is derived from the URL
          pathname via TAB_MAP above. Clients still snap to 'communicate'
          via the useEffect below. */}

      {/* Pipeline Tabs (shared across status/tasks/tracker) */}
      {!isClient && ['status', 'tasks', 'tracker'].includes(tab) && (
        <PipelineTabs tasks={tasks} activeTab={deptTab} onTabChange={setDeptTab} showAll={true} />
      )}

      {/* ══ CURRENT STATUS ══ */}
      {tab === 'status' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-xl font-heading font-bold text-white">{filtered.length}</p>
              <p className="text-[10px] text-zinc-500">Total Assigned</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-xl font-heading font-bold text-emerald-400">{completed.length}</p>
              <p className="text-[10px] text-zinc-500">Completed</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-xl font-heading font-bold text-blue-400">{inProgress.length}</p>
              <p className="text-[10px] text-zinc-500">In Progress</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-xl font-heading font-bold text-violet-400">{review.length}</p>
              <p className="text-[10px] text-zinc-500">In Review</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-xl font-heading font-bold text-red-400">{blocked.length}</p>
              <p className="text-[10px] text-zinc-500">Blocked</p>
            </div>
          </div>

          <p className="text-[10px] text-amber-400 flex items-center gap-1"><Activity className="w-3 h-3" /> Every status update is instantly visible to VawWorld production team</p>

          {/* Task Columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-zinc-900 border border-blue-500/20 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <h3 className="text-xs font-semibold text-zinc-300 uppercase">In Progress ({inProgress.length})</h3>
              </div>
              <div className="divide-y divide-zinc-800/30 max-h-[40vh] overflow-y-auto">
                {inProgress.map(t => (
                  <div key={t.id} className="px-4 py-3">
                    <p className="text-xs text-zinc-200 font-medium">{taskLabel(t)}</p>
                    <div className="flex items-center gap-2 mt-1"><span className="text-[9px] text-zinc-500">{t.task_type}</span><span className="text-[9px] font-mono text-zinc-600">Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : '-'}</span></div>
                    <div className="flex items-center gap-2 mt-2"><div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${t.progress}%` }} /></div><span className="text-[9px] font-mono text-zinc-500">{t.progress}%</span></div>
                    <div className="flex gap-1.5 mt-2">
                      <button onClick={() => updateTask(t.id, 'review', 100)} disabled={updating === t.id} className="h-6 px-2 bg-violet-500/20 text-violet-400 text-[9px] rounded-sm hover:bg-violet-500/30 flex items-center gap-1"><Send className="w-3 h-3" /> Submit</button>
                      <button onClick={() => updateTask(t.id, 'completed', 100)} disabled={updating === t.id} className="h-6 px-2 bg-emerald-500/20 text-emerald-400 text-[9px] rounded-sm hover:bg-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Done</button>
                    </div>
                  </div>
                ))}
                {inProgress.length === 0 && <p className="text-xs text-zinc-600 text-center py-6">No tasks in progress</p>}
              </div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-800"><h3 className="text-xs font-semibold text-zinc-300 uppercase">Pending ({pending.length + blocked.length})</h3></div>
              <div className="divide-y divide-zinc-800/30 max-h-[40vh] overflow-y-auto">
                {pending.map(t => (
                  <div key={t.id} className="px-4 py-3">
                    <p className="text-xs text-zinc-300">{taskLabel(t)}</p>
                    <span className="text-[9px] text-zinc-600">{t.task_type}</span>
                    <button onClick={() => updateTask(t.id, 'in_progress', 10)} className="mt-2 h-6 px-2 bg-blue-500/20 text-blue-400 text-[9px] rounded-sm flex items-center gap-1"><Play className="w-3 h-3" /> Start</button>
                  </div>
                ))}
                {blocked.map(t => (<div key={t.id} className="px-4 py-3 bg-red-500/5"><p className="text-xs text-red-300">{taskLabel(t)}</p><p className="text-[9px] text-red-400">BLOCKED</p></div>))}
              </div>
            </div>
            <div className="bg-zinc-900 border border-emerald-500/20 rounded-sm">
              <div className="px-4 py-3 border-b border-zinc-800"><h3 className="text-xs font-semibold text-zinc-300 uppercase">Done ({completed.length + review.length})</h3></div>
              <div className="divide-y divide-zinc-800/30 max-h-[40vh] overflow-y-auto">
                {review.map(t => (<div key={t.id} className="px-4 py-3 bg-violet-500/5"><p className="text-xs text-zinc-200">{taskLabel(t)} <span className="text-[8px] px-1.5 py-0.5 bg-violet-500/20 text-violet-400 rounded">In Review</span></p></div>))}
                {completed.map(t => (<div key={t.id} className="px-4 py-3"><div className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /><p className="text-xs text-zinc-400">{taskLabel(t)}</p></div><span className="text-[9px] font-mono text-emerald-500 ml-5">{t.rate_amount?.toLocaleString()}</span></div>))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MY TASKS (Phase 2 — May 23 2026) ══
          Founder spec: canonical-named shot rows, inline SUBMIT button,
          strict naming enforcement at upload. Replaces the legacy
          WorkCard grid which was too cluttered. */}
      {tab === 'tasks' && (
        <div className="space-y-3" data-testid="fl-my-tasks">
          <div className="bg-zinc-950 border border-amber-500/30 rounded-md p-3 text-[11px] text-amber-100 flex items-start gap-2">
            <span className="text-amber-300">⚡</span>
            <p>
              <strong className="text-amber-200">Strict naming:</strong> the canonical filename below each row is the only name your upload will accept.
              Save your file with that exact name (click to copy), then hit Submit.
              Wrong names are rejected with a clear error so you can rename and retry.
            </p>
          </div>
          <MyTasksList
            tasks={(filtered || []).map(t => ({
              ...t,
              // The shared MY TASKS row expects these fields. Adapt the
              // legacy /tasks shape to the canonical shape.
              project_name: t.project_name || t.project?.name,
              work_order_no: t.work_order_no || t.wo_no,
              stage: t.stage || t.task_type,
            }))}
            onSubmitted={() => load()}
          />
        </div>
      )}

      {/* ══ REFERENCES ══ */}
      {tab === 'references' && (
        <ReferencesPanel />
      )}

      {/* ══ TRACKER ══ */}
      {tab === 'tracker' && (
        <div className="space-y-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 h-4 bg-zinc-800 rounded-sm overflow-hidden flex">
                {['completed','review','in_progress','not_started','blocked'].map(s => {
                  const c = filtered.filter(t => t.status === s).length;
                  const p = filtered.length > 0 ? (c / filtered.length * 100) : 0;
                  const colors = { completed: '#10b981', review: '#8b5cf6', in_progress: '#3b82f6', not_started: '#71717a', blocked: '#ef4444' };
                  return p > 0 ? <div key={s} style={{ width: `${p}%`, backgroundColor: colors[s] }} /> : null;
                })}
              </div>
              <span className="text-lg font-heading font-bold text-white">{filtered.length > 0 ? Math.round(completed.length / filtered.length * 100) : 0}%</span>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-zinc-800 text-[10px] text-zinc-500 uppercase"><th className="text-left px-4 py-2">Shot</th><th className="text-left px-4 py-2">Dept</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Progress</th><th className="text-left px-4 py-2">Due</th><th className="text-left px-4 py-2">Value</th></tr></thead>
              <tbody className="divide-y divide-zinc-800/30">
                {filtered.map(t => (
                  <tr key={t.id} className="hover:bg-zinc-800/20">
                    <td className="px-4 py-2 text-xs text-zinc-200">{taskLabel(t)}</td>
                    <td className="px-4 py-2 text-[10px] text-zinc-500 capitalize">{t.task_type}</td>
                    <td className="px-4 py-2"><span className={`status-${t.status} px-1.5 py-0.5 rounded-full text-[9px]`}>{t.status?.replace('_',' ')}</span></td>
                    <td className="px-4 py-2"><div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${t.progress}%` }} /></div><span className="text-[9px] font-mono text-zinc-500">{t.progress}%</span></div></td>
                    <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{t.due_date ? new Date(t.due_date).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-2 text-[10px] font-mono text-emerald-400">{t.rate_amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ EARNINGS ══ */}
      {tab === 'earnings' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Earnings & Invoices</h2>
            <button onClick={() => { setEditingInvoice(null); setShowInvoiceBuilder(true); }} data-testid="freelancer-raise-invoice-btn"
              disabled={acceptedWOs.length === 0}
              className="h-9 px-4 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 text-xs font-bold rounded-md flex items-center gap-2 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Raise Invoice
            </button>
          </div>
          {acceptedWOs.length === 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-md p-3 flex items-start gap-2 text-xs text-amber-300">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>You need an <strong>accepted</strong> Work Order to raise an invoice.</span>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-2xl font-heading font-bold text-emerald-400 font-mono">{totalEarned.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
              <p className="text-[10px] text-zinc-500">Total Earned</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-2xl font-heading font-bold text-blue-400">{completed.length}</p>
              <p className="text-[10px] text-zinc-500">Tasks Completed</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-center">
              <p className="text-2xl font-heading font-bold text-amber-400">{inProgress.length + pending.length}</p>
              <p className="text-[10px] text-zinc-500">Pending Earnings</p>
            </div>
          </div>

          {/* Invoice list */}
          <InvoiceList
            invoices={invoices}
            onEdit={(inv) => { setEditingInvoice(inv); setShowInvoiceBuilder(true); }}
            onDelete={async (inv) => {
              if (!window.confirm(`Delete draft invoice ${inv.invoice_no}?`)) return;
              try { await api.delete(`/invoices/${inv.id}`); await loadInvoices(); } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
            }}
          />

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-800"><h3 className="text-xs font-semibold text-zinc-300 uppercase">Completed Work</h3></div>
            <div className="divide-y divide-zinc-800/30">
              {completed.map(t => (
                <div key={t.id} className="px-4 py-2.5 flex items-center justify-between">
                  <div><p className="text-xs text-zinc-300">{taskLabel(t)}</p><p className="text-[10px] text-zinc-600">{t.task_type} | {t.completed_at ? new Date(t.completed_at).toLocaleDateString() : ''}</p></div>
                  <span className="text-xs font-mono text-emerald-400">{t.rate_amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Modal */}
          <InvoiceBuilderModal
            open={showInvoiceBuilder}
            onClose={() => { setShowInvoiceBuilder(false); setEditingInvoice(null); }}
            workOrders={workOrders}
            editingInvoice={editingInvoice}
            onCreated={() => { loadInvoices(); }}
          />
        </div>
      )}

      {/* ══ COMMUNICATE ══ */}
      {tab === 'communicate' && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-400" /> Message Production Manager</h3>
            <div className="flex gap-2">
              <input value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Type your message..." className="flex-1 h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" />
              <button onClick={sendMessage} disabled={!msgText.trim()} className="h-9 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm disabled:opacity-40 flex items-center gap-1"><Send className="w-3.5 h-3.5" /> Send</button>
            </div>
            {msgSent && <p className="text-[10px] text-emerald-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Message sent to production team</p>}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-3 flex items-center gap-2"><Ticket className="w-4 h-4 text-amber-400" /> Raise a Ticket</h3>
            <p className="text-[10px] text-zinc-500 mb-2">For blocked tasks, payment issues, scope clarification, or any concerns</p>
            <div className="flex gap-2">
              <input value={ticketText} onChange={e => setTicketText(e.target.value)} placeholder="Describe your issue..." className="flex-1 h-9 bg-zinc-950 border border-zinc-800 rounded-sm px-3 text-xs text-white focus:ring-1 focus:ring-blue-500 focus:outline-none" />
              <button onClick={raiseTicket} disabled={!ticketText.trim()} className="h-9 px-4 bg-amber-600 hover:bg-amber-500 text-white text-xs rounded-sm disabled:opacity-40 flex items-center gap-1"><Ticket className="w-3.5 h-3.5" /> Raise Ticket</button>
            </div>
            {ticketSent && <p className="text-[10px] text-emerald-400 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ticket raised. Production team will review it.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
